import ApiService from './api';

/** Channel information attached to a recently completed form. */
export interface RecentlyCompletedFormBot {
  /** The display name of the channel (populated after API request) */
  channelDisplay?: string | null;
  /** The display name of the channel */
  channel: string;
  /** The ID of the channel */
  channelId: string;
  /** The ID of the bot */
  botId: string;
}

/** One answered step within a recently completed form. */
export interface RecentlyCompletedFormStep {
  /** The name of the step in the UI */
  stepName: string;
  /** The template used for this step, which lets you know the type for context value */
  template: string;
  /** The rule used for this step. Useful for `BotFormValidableInput` to know what it's trying to validate (phone number, license plate, etc.) */
  rule?: string | null;
  /** The technical name of the context ("technical name" in UI) */
  contextName?: string | null;
  /** The value the user responded with */
  contextValue?: string | null;
  /** An ISO date-string of when the answer was given */
  contextDate?: string | null;
}

/** A bot form the user recently completed, including all answered steps. */
export interface RecentlyCompletedForm {
  /** The technical name of the form */
  name: string;
  /** The display name of the form */
  displayName: string;
  /** The steps of the form */
  steps: RecentlyCompletedFormStep[];
  /** Information about the bot (channel) */
  bot: RecentlyCompletedFormBot;
}

const BotFormsService = {
  // startDate & endDate in format 'yyyy-mm-dd'
  async getFormInsights({ botId, channelId, startDate, endDate, steps, intent }: {
    /** ID of the bot */
    botId: string;
    /** Optional channel ID to scope the stats to */
    channelId?: string | null;
    /** Start of the reporting period, format 'yyyy-mm-dd' */
    startDate: string;
    /** End of the reporting period, format 'yyyy-mm-dd' */
    endDate: string;
    /** Form steps to compute insights for */
    steps: any;
    /** Intent the form belongs to */
    intent: string;
  }): Promise<any> {
    try {
      const url = `/schaltzentrale/stats/botforms/${botId}/${channelId || ''}?startTime=${startDate}&endTime=${endDate}`;

      const response = await ApiService.post(url, {
        steps,
        intent,
        include_userIds: true,
      });

      return response.data;
    } catch (e) {
      return false;
    }
  },
  // available in #431 moin-api,
  async getLeads({ botId, steps, users }: {
    /** ID of the bot */
    botId: string;
    /** Form steps to extract lead data from */
    steps: any;
    /** User IDs to fetch leads for */
    users: any;
  }): Promise<any> {
    try {
      const url = `/schaltzentrale/stats/botforms/${botId}/leads`;

      const response = await ApiService.post(url, {
        steps,
        users,
      });

      return response.data;
    } catch (e) {
      return false;
    }
  },
  /**
   * Searches for the most recent bot forms a user have completed
   * @param botId Staging or Live bot ID
   * @param uniqueUserId The chat widget user ID
   * @param count Number of forms to return.
   * `0` for no limit.
   * @returns Array of forms in descending order (newest to oldest).<br/>
   * If empty array, user has not completed any forms.<br/>
   * `null` if error.
   */
  async searchCompleted(botId: string, uniqueUserId: string, count: number = 1): Promise<null | RecentlyCompletedForm[]> {
    try {
      const url = `/schaltzentrale/forms/searchCompleted`;

      const response = await ApiService.post(url, {
        uniqueBotId: botId,
        uniqueUserId,
        count,
      });

      return response.data;
    } catch(e) {
      console.error(e);
      return null;
    }
  }
};

export default BotFormsService;
